// React
import { useEffect, useState } from "react"

// Third-Party Libraries
import axios from "axios"

// Types
import type { ErrorFetch, SuccessFetch } from "types"
import type { ProductReview } from "../../types"

// Utils
import { useApi } from "utils"

type FilteredParams = {
  page?: number
  keyword?: string
  limit?: number
  file_name?: string
  file_status?: number
}
type ListProductImport = {
  payload: ProductReview[],
  pagination_data: {
    total: number
    page: number
    size: number
    pages: number
  }
}

export function useDetail({ page, keyword, limit, file_name, file_status }: FilteredParams) {
  const defaultValue = {
    payload: [],
    pagination_data: {
      page: 0,
      pages: 0,
      size: 0,
      total: 0
    }
  }

  const api = useApi()
  const [data, setData] = useState<ListProductImport>(defaultValue)
  const [allData, setAllData] = useState<ProductReview[]>([])
  const [isLoading, setLoading] = useState(true)

  const params: { [key: string]: number | string | undefined } = {};
  if (page) params.page = page;
  if (keyword) params.keyword = keyword;
  if (limit) params.limit = limit;
  if (file_name) params.file_name = file_name;
  if (file_status) params.file_status = file_status;

  const fetchData = () => {
    setLoading(true)

    axios.all([
      api.get("/importexcel/viewfile", { params }),
      api.get("/importexcel/viewfile-all", { params })
    ]).then(axios.spread((res, resAll) => {
      setData(res.data)
      setAllData(resAll.data.payload)
    })).catch(() => {
      setData(defaultValue)
      setAllData([])
    }).finally(() => {
      setLoading(false)
    })
  }

  const refetch = () => fetchData()

  useEffect(() => {
    fetchData()

    return () => {
      setData(defaultValue)
      setLoading(true)
    }

    // eslint-disable-next-line
  }, [page, keyword, limit, file_name, file_status])

  return { allData, data, isLoading, refetch }
}

export function useDropdownFileName() {
  const api = useApi()
  const [data, setData] = useState<{ file_name: string }[] | null>(null)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoading, setLoading] = useState(true)

  const refetch = () => {
    setLoading(true)

    api.get("/importexcel/filename")
    .then((res: SuccessFetch<{ payload: { file_name: string }[] }>) => setData(res.data.payload))
    .catch((err: ErrorFetch<{ detail: { message: string } }>) => setError(err.response.data.detail.message))
    .finally(() => setLoading(false))
  }

  useEffect(() => {
    api.get("/importexcel/filename")
    .then((res: SuccessFetch<{ payload: { file_name: string }[] }>) => setData(res.data.payload))
    .catch((err: ErrorFetch<{ detail: { message: string } }>) => setError(err.response.data.detail.message))
    .finally(() => setLoading(false))
  
    return () => {
      setData(null)
      setError(undefined)
      setLoading(true)
    }
    // eslint-disable-next-line
  }, [])

  return { data, error, isLoading, refetch }
}